/**
 * Pure eviction-ordering logic for the service worker's storage-pressure
 * policy (`src/sw.ts`'s `relieveStoragePressure`).
 *
 * Extracted from `sw.ts` purely so it can be tested: that module registers
 * `self.addEventListener` handlers and Workbox routes at import time, so it
 * cannot be imported into a test at all. Everything here is deliberately
 * dependency-free -- no `@config`, no DOM, no Workbox -- because `sw.ts` is
 * bundled by `vite-plugin-pwa`'s `injectManifest` in a reduced Vite
 * environment where this repo's plugins (and therefore `virtual:pwa-config`)
 * don't resolve.
 */

export interface AssetOwnerRecord {
	issueId: string;
	cacheName: string;
	/** ms since epoch; absent on records written before 2026-08-28. */
	savedAt?: number;
}

/**
 * Orders issues oldest-download-first, for eviction under storage pressure.
 *
 * An issue's age is the age of its *oldest* asset, not its newest: assets for
 * one issue arrive over multiple download passes (images eagerly, video and
 * audio lazily), so taking the newest would let a long-lived issue keep
 * refreshing its own timestamp and never become evictable.
 *
 * A missing `savedAt` is read as `0` — oldest — which is both the safe
 * default and factually right, since those records predate the field.
 */
export function buildEvictionQueue(
	entries: Array<[string, AssetOwnerRecord]>,
): Array<string> {
	const oldestByIssue = new Map<string, number>();

	for (const [, record] of entries) {
		const savedAt = record.savedAt ?? 0;
		const current = oldestByIssue.get(record.issueId);
		if (current === undefined || savedAt < current) {
			oldestByIssue.set(record.issueId, savedAt);
		}
	}

	return [...oldestByIssue.entries()]
		.sort((a, b) => a[1] - b[1])
		.map(([issueId]) => issueId);
}
